import dayjs from "dayjs";

import { injectsN2 } from "../../../../constants/ripeningProgramFields";
import { RipeningRoomRun, RipeningRoomStatus } from "../../../../hooks/useRipeningPrograms";
import { storeInjectsN2 } from "../../../../constants/ripeningStorePrograms";
import { getCycleProgram, getCycleStore, getLivePhaseNumber, sortedPhases } from "./runningProgram";

export type RoomAvailabilityKind =
    | "checking"
    | "free"
    | "pallets_inside"
    | "cycle_open"
    | "ripening"
    | "storage"
    | "paused"
    | "no_cycle"
    | "done"
    | "emergency_stop"
    | "no_connection"
    | "no_update"
    | "no_status"
    | "unavailable"
    | "unlinked";

export type RoomAvailabilityColor = "success" | "primary" | "info" | "warning" | "danger" | "secondary" | "purple";

export interface RoomAvailability {
    kind: RoomAvailabilityKind;
    // * Answers "can I use this room?" first, then what is going on.
    label: string;
    detail: string | null;
    // * What the user should do next, when anything.
    next: string | null;
    color: RoomAvailabilityColor;
}

export interface RoomAvailabilityInput {
    // * The room has a live status source (gateway + room id on the location).
    linked: boolean;
    // * Some room of this client has a live status source.
    clientHasLinkedRooms: boolean;
    // * The live status; undefined while it loads.
    status: RipeningRoomStatus | undefined;
    // * The live status, with a just-confirmed start shown as running.
    run: RipeningRoomRun | null | undefined;
    statusError: boolean;
    // * Pallets in the room today; null while unknown.
    palletCount: number | null;
    hasActiveCycle: boolean;
    now?: dayjs.Dayjs;
}

// * The room republishes its status at least every minute; after 15 minutes without one, it is out of date.
export const STATUS_STALE_AFTER_MS = 15 * 60 * 1000;

const pluralPallets = (count: number) => `${count} ${count === 1 ? "pallet" : "pallets"}`;

/** "14:02" today, "Fri 14:00" within a week, "6 Oct 09:30" otherwise. */
export function formatRoomTime(at: string | null | undefined, now: dayjs.Dayjs = dayjs()): string | null {
    if (!at) return null;
    const time = dayjs(at);
    if (!time.isValid()) return null;
    if (time.isSame(now, "day")) return time.format("HH:mm");
    if (Math.abs(time.diff(now, "day", true)) < 6) return time.format("ddd HH:mm");
    return time.format("D MMM HH:mm");
}

const result = (kind: RoomAvailabilityKind, color: RoomAvailabilityColor, label: string, detail: string | null = null, next: string | null = null): RoomAvailability => ({
    kind, color, label, detail, next,
});

/** What the room is doing and whether it can be used, for the room status card.
 *  null: show nothing (the client has no live room status at all). */
export function getRoomAvailability({
    linked,
    clientHasLinkedRooms,
    status,
    run,
    statusError,
    palletCount,
    hasActiveCycle,
    now = dayjs(),
}: RoomAvailabilityInput): RoomAvailability | null {
    if (!linked) {
        if (!clientHasLinkedRooms) return null;
        return result("unlinked", "secondary", "Not linked", "This room is not linked yet, so we can't show what it is doing.");
    }

    const state = String(run?.state ?? "").toLowerCase();
    const running = ["ripening", "storage", "paused"].includes(state);

    // * A start the room just confirmed counts even before the room reports it.
    if (!running) {
        if (statusError) return result("unavailable", "secondary", "Status unavailable", "We can't load the room status right now.");
        if (!status) return result("checking", "secondary", "Checking status…");
        if (status.status === "unknown") return result("no_status", "secondary", "No live status yet", "The room has not reported anything yet.");
    }

    if (status?.status === "stale") {
        const since = formatRoomTime(status.last_raw_at, now);
        return result("no_connection", "danger", since ? `No connection since ${since}` : "No connection", "We can't see what the room is doing.");
    }

    if (status?.status === "online" && status.last_raw_at && dayjs(status.last_raw_at).isValid()
        && now.diff(dayjs(status.last_raw_at)) > STATUS_STALE_AFTER_MS) {
        return result(
            "no_update",
            "danger",
            `No update since ${formatRoomTime(status.last_raw_at, now)}`,
            "The room has stopped reporting. What it shows may be out of date."
        );
    }

    if (state === "emergency_stop") {
        return result("emergency_stop", "danger", "Emergency stop", "The room stopped itself.", "Check the room on site.");
    }

    // * EFQ is where a room's cycle lives: a room that runs without one is out of sight.
    if (running && !hasActiveCycle) {
        return result(
            "no_cycle",
            "danger",
            "Running without a cycle in EFQ",
            "The room runs a program, but no ripening cycle is open in EFQ.",
            "Start a cycle in EFQ, or stop the room."
        );
    }
    // * A store never finishes: it holds until it is stopped.
    if (running && state !== "storage" && run?.program_finished) {
        if (palletCount !== null && palletCount > 0) {
            return result("done", "purple", "Ripening done: ready to unload", "Keeping the last temperature until the cycle ends.", "Unload the room, then end the cycle.");
        }
        return result("done", "purple", "Ripening done: ready to end the cycle", "Keeping the last temperature until the cycle ends.", "End the cycle.");
    }
    if (state === "ripening") return result("ripening", "primary", "In use: ripening");
    if (state === "storage") return result("storage", "info", "In use: storage", "Keeping storage conditions.");
    if (state === "paused") return result("paused", "warning", "In use: paused", "The program is paused.");

    // * Nothing running: the room is free once it is empty and no cycle is open.
    if (palletCount === null) return result("checking", "secondary", "Checking status…");
    if (hasActiveCycle) {
        return palletCount > 0
            ? result("cycle_open", "warning", "Not free: cycle open", `A ripening cycle is open with ${pluralPallets(palletCount)}, but nothing is running.`)
            : result("cycle_open", "warning", "Not free: cycle open", "A ripening cycle is open, but the room is empty and nothing is running.", "End the cycle.");
    }
    if (palletCount > 0) {
        return result("pallets_inside", "warning", "Not free: pallets inside", `${pluralPallets(palletCount)} in the room, nothing running.`, "Start a cycle.");
    }
    return result("free", "success", "Free to use", "Room is empty, nothing running.");
}

export interface LowOxygen {
    // * The blue low-oxygen lamp at the door is on.
    lampOn: boolean;
}

/**
 * Low oxygen in the room: N2 injection runs or the blue lamp at the door is on; null otherwise.
 * When the room does not say whether N2 runs, the active cycle's program answers it for the
 * phase the room is in, or for the store it holds.
 */
export function getLowOxygen(run: RipeningRoomRun | null | undefined, cycleMeta?: any): LowOxygen | null {
    if (!run) return null;
    const lampOn = run.low_o2_lamp === true;
    let n2Active = run.n2_active === true;
    const state = String(run.state ?? "").toLowerCase();
    const n2Unknown = run.n2_active === undefined || run.n2_active === null;
    const store = n2Unknown && ["storage", "paused"].includes(state) ? getCycleStore(cycleMeta) : null;
    if (store) {
        n2Active = storeInjectsN2(store);
    } else if (n2Unknown && ["ripening", "paused"].includes(state)) {
        const program = getCycleProgram(cycleMeta);
        if (program) {
            const phases = sortedPhases(program);
            const livePhase = getLivePhaseNumber(run, phases);
            n2Active = livePhase !== null && injectsN2(phases.find((phase) => phase.phase === livePhase)?.values);
        }
    }
    return lampOn || n2Active ? { lampOn } : null;
}
